import React, { useState, useEffect } from 'react';
import { Card, Button, Dropdown, Alert } from 'react-bootstrap';
import { useAccount } from 'wagmi';
import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
import { isAddress } from 'viem';
import { useNavigate } from 'react-router-dom';
import membersContractABI from '../contracts/tgetherMembers.json';
import * as config from '../config.tsx';
import useFetchPosts from './hooks/useFetchPosts.tsx';
import Post from './Items/Post.tsx';

const membersAddress = process.env.REACT_APP_MEMBERS_CONTRACT_ADDRESS as `0x${string}`;

function MemberSearch({ value }: { value: string }) {
  const { address: account } = useAccount(); // Use `useAccount` to get the current user's address
  const navigate = useNavigate(); // Use `useNavigate` for navigation
  const [communities, setCommunities] = useState<string[]>([]);
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [credInfo, setCredInfo] = useState<number | null>(null);
  const [credIndex, setCredIndex] = useState<number | null>(null);
  const [isMember, setIsMember] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [showAlert, setShowAlert] = useState(false); // State for showing the pending transaction alert

  // Fetch posts associated with `value`
  const { posts, loading: postsLoading } = useFetchPosts(value);

  useEffect(() => {
    if (isAddress(value)) {
      setError(''); // Clear any previous error
      fetchMemberCommunities();
    } else {
      setError('Invalid Ethereum address provided.');
    }
  }, [value]);


  const fetchMemberCommunities = async () => {
    try {
      const communityNames = await readContract(config.config,{
        address: membersAddress,
        abi: membersContractABI.abi,
        functionName: 'getMemberCommunities',
        args: [value],
      });
      const coms = communityNames as string[]
      // Extract the relevant community names, skipping metadata elements
      setCommunities(coms.slice(1));
    } catch (error) {
      console.error('Error fetching member communities:', error);
      setError('Failed to fetch member communities.');
    }
  };

  const handleCommunitySelect = (community: string) => {
    setSelectedCommunity(community);
    fetchCredInformation(community);
    checkCredGivenByCurrentUser(community);
    checkIsMember(community);
  };

  const fetchCredInformation = async (community: string) => {
    try {
      const _credInfo = await readContract(config.config,{
        address: membersAddress,
        abi: membersContractABI.abi,
        functionName: 'getMemberCreds',
        args: [value, community],
      });
      setCredInfo(Number(_credInfo));
    } catch (error) {
      console.error('Error fetching cred information:', error);
    }
  };

  const checkCredGivenByCurrentUser = async (community: string) => {
    if (!account) return; // If no account is provided, we cannot check cred status

    try {
      const _credIndex = await readContract(config.config, {
        address: membersAddress,
        abi: membersContractABI.abi,
        functionName: 'getMemberCredIndex',
        args: [value, account, community],
      });

      const crdind = _credIndex as string[];
      setCredIndex(Number(crdind[0])); // Extract the first value from the array
    } catch (error) {
      console.error('Error checking user cred status:', error);
    }
  };

  const checkIsMember = async (community: string) => {
    if (!account) return;

    try {
      const _isMember = await readContract(config.config,{
    
        address: membersAddress,
        abi: membersContractABI.abi,
        functionName: 'getIsMember',
        args: [account, community],
      });
      setIsMember(Boolean(_isMember));
    } catch (error) {
      console.error('Error checking membership status:', error);
    }
  };

  const handleGiveCred = async (isPos: boolean) => {
    try {
      const functionName = isPos ? 'addPosCredsToMember' : 'addNegCredsToMember';

      setShowAlert(true); // Show the pending transaction alert

      const tx = await writeContract(config.config, {
        address: membersAddress,
        abi: membersContractABI.abi,
        functionName,
        args: [selectedCommunity, value],
      });

      // Wait for transaction receipt
      await waitForTransactionReceipt(config.config, { hash: tx });

      // Fetch the latest information after the transaction is confirmed
      fetchCredInformation(selectedCommunity);
      checkCredGivenByCurrentUser(selectedCommunity);
      
      setShowAlert(false); // Hide the pending transaction alert

      // Navigate to the search results
      navigate(`/search-results?type=Member&value=${value}`);
    } catch (error) {
      console.error('Error giving cred:', error);
      setShowAlert(false); // Hide the alert in case of an error
    }
  };

  const handleRemoveCred = async (isPos: boolean) => {
    try {
      const functionName = isPos ? 'removePosCredsFromMember' : 'removeNegCredsFromMember';

      setShowAlert(true); // Show the pending transaction alert

      const tx = await writeContract(config.config, {
        address: membersAddress,
        abi: membersContractABI.abi,
        functionName,
        args: [selectedCommunity, value],
      });

      // Wait for transaction receipt
      await waitForTransactionReceipt(config.config, { hash: tx });

      // Fetch the latest information after the transaction is confirmed
      fetchCredInformation(selectedCommunity);
      checkCredGivenByCurrentUser(selectedCommunity);
      
      setShowAlert(false); // Hide the pending transaction alert

      // Navigate to the search results
      navigate(`/search-results?type=Member&value=${value}`);
    } catch (error) {
      console.error('Error removing cred:', error);
      setShowAlert(false); // Hide the alert in case of an error
    }
  };

  return (
    <Card className="mb-3" style={{ backgroundColor: '#2C2C2C', color: '#FFFFFF', borderColor: '#5A5A5A' }}>
      <Card.Header>
        <Dropdown  className="d-flex justify-content-center mt-3">
          <Dropdown.Toggle variant="info" id="dropdown-basic" >
            {selectedCommunity ? selectedCommunity : 'Select Community'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {communities.length > 0 ? (
              communities.map((community) => (
                <Dropdown.Item key={community} onClick={() => handleCommunitySelect(community)}>
                  {community}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item>No communities found</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {showAlert && <Alert variant="warning">Transaction is pending...</Alert>}
        {selectedCommunity && !error && (
          <>
            <div>
              <h5>Creds: {credInfo}</h5>
            </div>
            <div className="community-cred-info">
              <span className="community-cred-label">Creds:</span>
              <span className={`community-cred-value ${credInfo && credInfo > 0 ? 'text-success' : credInfo && credInfo < 0 ? 'text-danger' : ''}`}>
                {credInfo}
              </span>
              {isMember && (
                <>
                  <div className="mb-3">Member Status: <strong>{isMember ? 'Yes' : 'No'}</strong></div>
                  <div className="mb-3">
                    {credIndex !== null && credIndex > 0 && (
                      <span className="text-success">+ Positive</span>
                    )}
                    {credIndex !== null && credIndex < 0 && (
                      <span className="text-danger">- Negative</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant={credIndex !== null && credIndex > 0 ? 'success' : 'outline-success'}
                      onClick={credIndex !== null && credIndex > 0 ? () => handleRemoveCred(true) : () => handleGiveCred(true)}
                      disabled={credIndex !== null && credIndex < 0}
                      className="me-2"
                    >
                      +
                    </Button>
                    <Button
                      variant={credIndex !== null && credIndex < 0 ? 'danger' : 'outline-danger'}
                      onClick={credIndex !== null && credIndex < 0 ? () => handleRemoveCred(false) : () => handleGiveCred(false)}
                      disabled={credIndex !== null && credIndex > 0}
                    >
                      -
                    </Button>
                  </div>
                </>
              )}
              {!isMember && (
                <div className="text-muted">You need to be a member of the community to give cred.</div>
              )}
            </div>
          </>
        )}
        <hr className="my-4" style={{ borderColor: '#5A5A5A' }} />
        <h4 className="text-center">Posts</h4>
        {!error && postsLoading ? (
          <div>Loading posts...</div>
        ) : (
          posts.map((post) => (
            <Post key={post.tokenId} post={post} />
          ))
        )}
      </Card.Body>
    </Card>
  );
}

export default MemberSearch;